import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { ArrowRight, ArrowUpRight, MessageCircle } from "lucide-react";
import { PA_BASE, IMAGES, img, waLink, CABIN_IMAGE } from "../config";
import { KineticHero } from "../components/KineticHero";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal, Marquee, Overline } from "../components/motion";
import { AIRCRAFT_CATEGORIES, AIRCRAFT } from "../data/aircraft";
import { ROUTES } from "../data/routes";

const CHAPTERS = [
  {
    n: "01",
    title: "We arrange. You fly.",
    body: "JS Private Aviation is a charter brokerage. We don't own aircraft — we source the right one for each trip from a global network of independent, licensed operators, and manage the detail end to end.",
    image: CABIN_IMAGE,
  },
  {
    n: "02",
    title: "A network, not a fleet.",
    body: "Because we are tied to no fleet, our only obligation is the right aircraft for your route, dates and budget — quoted individually, every time. No instant prices, no false promises of availability.",
    image: img(IMAGES.jetFlying, 1000),
  },
  {
    n: "03",
    title: "Discretion as standard.",
    body: "Private terminals, minimal touchpoints and a charter team that handles your itinerary quietly. Who is on board, and where they are going, stays that way.",
    image: img(IMAGES.window, 1000),
  },
];

export default function HomePage() {
  return (
    <>
      <Seo
        title="JS Private Aviation | Private Jet Charter"
        description="Global access to private aircraft, arranged around your schedule. A private jet charter brokerage with a worldwide network of independent operators."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "JS Private Aviation",
          description: "Private jet charter brokerage arranging flights through independent aircraft operators.",
          parentOrganization: "JS Group Holdings",
        }}
      />
      <KineticHero
        testId="home-hero"
        overline="JS Private Aviation — Global Charter Brokerage"
        lines={["PRIVATE AVIATION,", "SIMPLIFIED."]}
        sub="Global access to private aircraft, arranged around your schedule."
        image={img(IMAGES.heroTarmac, 2000)}
      >
        <a
          href="#request"
          data-testid="hero-request-charter-cta"
          className="group inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
        >
          Request a Charter
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
        <a
          href={waLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-whatsapp-cta"
          className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <MessageCircle size={15} /> Speak to a Charter Specialist
        </a>
      </KineticHero>

      <section className="border-b border-black/10 bg-[#F1EFE8] py-8 text-black/70">
        <Marquee items={["London", "Dubai", "Maldives", "Singapore", "Mumbai", "Delhi", "Farnborough", "Velana"]} />
      </section>

      <section className="bg-[#F9F9F6] py-28 sm:py-36">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <p className="max-w-4xl font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-black/90">
              We arrange private charter through a worldwide network of independent aircraft operators —
              sourcing the right aircraft for each journey, quoted individually, around your schedule.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to={`${PA_BASE}/about`}
              data-testid="home-about-link"
              className="mt-10 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-black/60 transition-colors duration-300 hover:text-black"
            >
              How a brokerage works <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section id="request" data-testid="home-enquiry-section" className="bg-[#F9F9F6] pb-28 sm:pb-36">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
          <Reveal>
            <EnquiryForm source="homepage" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-28 sm:py-36 text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal>
            <Overline dark>The way we work</Overline>
          </Reveal>
          <div className="mt-16 space-y-24">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.05 * i}>
                <div
                  data-testid={`manifesto-chapter-${c.n}`}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-center ${
                    i % 2 === 1 ? "md:[direction:rtl]" : ""
                  }`}
                >
                  <div className="md:col-span-5 md:[direction:ltr]">
                    <div className="overflow-hidden">
                      <img
                        src={c.image}
                        alt={c.title}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 md:col-start-7 md:[direction:ltr]">
                    <span className="font-mono text-sm text-white/35">{c.n}</span>
                    <h3 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">{c.title}</h3>
                    <p className="mt-5 max-w-md text-[15px] leading-relaxed text-white/60">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F9F9F6] py-28 sm:py-36">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <Overline>Aircraft</Overline>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
                Every category, one network.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to={`${PA_BASE}/aircraft`}
                data-testid="home-aircraft-link"
                className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-black/60 transition-colors duration-300 hover:text-black"
              >
                View all aircraft <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AIRCRAFT_CATEGORIES.map((cat, i) => {
              const example = AIRCRAFT.find((a) => a.category === cat);
              return (
                <Reveal key={cat} delay={0.05 * i}>
                  <Link
                    to={`${PA_BASE}/aircraft`}
                    data-testid={`category-card-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group block border border-black/10 bg-white"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={example?.image}
                        alt={example ? `${cat} — ${example.name}` : cat}
                        loading="lazy"
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="flex items-center justify-between p-6">
                      <div>
                        <h3 className="font-display text-xl tracking-tight">{cat}</h3>
                        {example && (
                          <p className="mt-1 text-[12px] text-black/45">e.g. {example.name}</p>
                        )}
                      </div>
                      <ArrowUpRight
                        size={18}
                        className="text-black/30 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-black"
                      />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-[12px] leading-relaxed text-black/40">
              Aircraft examples available through our global operator network. JS Private Aviation arranges
              charter through independent operators and does not own or operate aircraft.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-28 sm:py-36 text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <Overline dark>Frequently arranged</Overline>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">Routes we know well.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to={`${PA_BASE}/destinations`}
                data-testid="home-destinations-link"
                className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-white/50 transition-colors duration-300 hover:text-white"
              >
                All destinations <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14">
            {ROUTES.map((r, i) => (
              <Reveal key={r.slug} delay={0.04 * i}>
                <Link
                  to={`${PA_BASE}/routes/${r.slug}`}
                  data-testid={`route-row-${r.slug}`}
                  className="group flex items-center justify-between border-t border-white/10 py-7 transition-colors duration-300 last:border-b hover:bg-white/[0.03]"
                >
                  <div className="flex items-baseline gap-5 sm:gap-8 px-2">
                    <span className="font-mono text-[11px] text-white/30">0{i + 1}</span>
                    <span className="font-display text-2xl sm:text-3xl tracking-tight">
                      {r.from} <span className="text-white/35">→</span> {r.to}
                    </span>
                  </div>
                  <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.25em] text-white/35 px-2">
                    {r.categories[0]}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F9F9F6] py-28 sm:py-36">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 md:grid-cols-2 gap-12 px-5 sm:px-8 items-center">
          <Reveal>
            <div className="overflow-hidden">
              <img
                src={img(IMAGES.wingClouds, 1100)}
                alt="Wing above cloud"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Empty legs</Overline>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
              Repositioning flights, when they exist.
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-black/60">
              When an operator repositions an aircraft, the empty sector can occasionally be chartered.
              We list genuine empty legs as our network releases them — never manufactured inventory.
            </p>
            <Link
              to={`${PA_BASE}/empty-legs`}
              data-testid="home-empty-legs-cta"
              className="mt-8 inline-flex items-center gap-3 border border-black px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-black hover:text-white"
            >
              View empty legs <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-28 sm:py-36 text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 text-left">
          <Reveal>
            <Overline dark>Begin</Overline>
            <h2 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Speak with a charter specialist.
            </h2>
            <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-white/60">
              One conversation is usually enough to know what your trip needs. Our team reviews every
              enquiry personally.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
              >
                <MessageCircle size={15} /> WhatsApp a Charter Specialist
              </a>
              <Link
                to={`${PA_BASE}/contact`}
                data-testid="final-contact-cta"
                className="border border-white/30 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
              >
                Contact us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
